import type { LanguageCode } from '@/types/api';

/**
 * Default allowed values for economic_sector and grammatical_reference
 *
 * IMPORTANT: These are example values. Edit these constants to match
 * your actual valid values from your organization's taxonomy.
 *
 * - economic_sector: Language-agnostic sector names
 * - grammatical_reference: Language-specific grammatical structures (keyed by language code)
 */
export const DEFAULT_ALLOWED_VALUES = {
  /**
   * Economic sector defaults - language agnostic
   * Format: Simple strings representing sector names
   */
  economic_sector: [
    "Industry and Manufacturing",
    "Energy",
    "Environmental Management and Waste",
    "Construction and Infrastructure",
    "Trade and Distribution",
    "Transport and Logistics",
    "Tourism and Hospitality",
    "Technology",
    "Media and Communications",
    "Banking, Finance and Insurance",
    "Real Estate",
    "Consulting and Services",
    "Human Resources",
    "Public Sector and Defense",
    "Education and Training",
    "Health and Wellness",
    "Culture, Leisure and Entertainment",
    "Personal and Community Services",
    "Domestic and Household Work",
    "Legal",
    "Training and Teaching",
    "Pharmaceuticals",
    "Retail"
  ] as string[],

  /**
   * Grammatical reference defaults - language specific
   * Format: "CEFR_Level\tGrammar_Point"
   * Each language has its own set of grammatical structures
   */
  grammatical_reference: {
    en: [
      "A1\tSubject Pronouns",
      "A1\tPresent Simple of ‘Be’",
      "A1\tPossessive Adjectives",
      "A1\tNumbers",
      "A1\tSingular and Plural Nouns",
      "A1\tIrregular Plural Nouns",
      "A1\tPresent Simple",
      "A1\tAdverbs of Frequency",
      "A1\tObject Pronouns",
      "A1\tPossessive 's",
      "A1\tPrepositions of Place and Time",
      "A1\t\"Have\" and \"Has\"",
      "A1\tThere is/are affirmative",
      "A1\tThere is/are affirmative + negative + interrogative",
      "A1\tModal Verbs",
      "A1\tClosed Questions",
      "A1\tOpen Questions",
      "A1\tHow Much/How Many",
      "A1\tPresent Continuous",
      "A1\tComparatives",
      "A1\tSuperlatives",
      "A1\tDemonstrative Pronouns",
      "A1\tImperatives (+/-)",
      "A1\tPossessive Pronouns",
      "A1\tCommon and Demonstrative Adjectives",
      "A1\tNon-finite Verbs (Like Love Enjoy Hate Dislike)",
      "A1\tIntensifiers (very, really, quite)",
      "A1\tPast Simple of ‘Be’",
      "A1\tPast Simple",
      "A2\tArticles (a, an, the)",
      "A2\tPresent Simple versus Present Continuous",
      "A2\tCountable and Uncountable Nouns",
      "A2\tLike versus Would like",
      "A2\tQuantifiers - Some / Any",
      "A2\tSimple Past (Regular Verbs)",
      "A2\tSimple Past (Irregular Verbs)",
      "A2\tAdverbs of manner",
      "A2\tSuggestion – Could / Let’s / Shall",
      "A2\tAdverbs of time",
      "A2\tAdjective Order",
      "A2\tModals for Permission and Ability",
      "A2\tFuture Simple (will)",
      "A2\tQuestions – Subject",
      "A2\tNon-finite Verbs (Want / Need + To-infinitive)",
      "A2\tModals for Obligation",
      "A2\tBe able to",
      "A2\tStative Verbs",
      "A2\tQuantifiers - None / Every / All",
      "A2\tIndefinite pronouns - Someone / Anyone / No one / Everyone",
      "A2\tIndefinite pronouns - Something / Anything / Nothing / Everything",
      "A2\tComparatives for Equality",
      "A2\tFuture \"Going to\"",
      "A2\tModals of Advice",
      "A2\tAdverbial phrases of frequency, time and place",
      "A2\tQuestions – Object",
      "A2\tPresent Tenses for the Future",
      "A2\t1st Conditional",
      "A2\tModals of Possibility",
      "A2\tInseparable phrasal verbs",
      "B1\tFuture (Will vs. Going to)",
      "B1\tReflexive & Reciprocal Pronouns",
      "B1\tComparative & Superlative Adverbs",
      "B1\tPresent continuous for future arrangements",
      "B1\tPresent perfect simple",
      "B1\tAdverbs of Degree & Intensity",
      "B1\tAdjectives Ending in ‘-ing’ and ‘-ed’",
      "B1\tPresent perfect simple (still, yet, already)",
      "B1\tModal verbs Possibility & Deduction  (present)",
      "B1\tSeparable phrasal verbs",
      "B1\tPresent Perfect Continuous",
      "B1\tPrepositions of Time (during, before, after, by, until)",
      "B1\tA few and A little versus Few and Little",
      "B1\tConditionals (Zero and First)",
      "B1\tLots of / A lot of/ Plenty of/ Enough",
      "B1\tPast Continuous",
      "B1\tAdverb Clauses of Time (When/While)",
      "B1\tNoun Clauses – ‘That’ vs ‘WH’ Clauses",
      "B1\tRelative clauses (defining)",
      "B1\tGerunds & Infinitives as Subjects",
      "B1\tReported Speech (simple tenses)",
      "B1\tPassive voice (simple tenses)",
      "B1\tNo Article",
      "B1\tGerunds & Infinitives as Objects",
      "B1\tRelative clauses (non defining)",
      "B1\tQuestion Tags",
      "B1\tReported Speech –  Say & Tell",
      "B1\tPast habits using \"used to\" and \"would\"",
      "B1\tUsed to / Be Used to / Get Used to",
      "B1\tSecond conditional",
      "B2\tFuture Time Expressions (be about to, be supposed to)",
      "B2\tFuture Continuous",
      "B2\tAdverbs of Degree & Intensity",
      "B2\tFuture in the Past",
      "B2\tEmphasis Using Do/Did",
      "B2\tParticiple Clauses",
      "B2\tPassive Voice with Modals",
      "B2\tPassive Gerunds & Infinitives",
      "B2\tVerbs of the Senses",
      "B2\tCausative Verbs",
      "B2\tExpressions of Quantity",
      "B2\tIdiomatic Expressions with Modals",
      "B2\tReduced Relative Clauses",
      "B2\tSubjunctive Mood",
      "B2\tExpressions of Purpose",
      "B2\tReported Speech (backshifting tenses)",
      "B2\tSay and Tell",
      "B2\tThird conditional",
      "B2\tPast Perfect",
      "B2\tAdverbial Clauses of Concession",
      "B2\t“Used to” vs. “Would”",
      "B2\tExpressing Regret and Criticism",
      "B2\tModals for Speculation (Past)",
      "B2\tAdverbial Clauses of Contrast and Comparison",
      "B2\tReported Speech (Questions, Requests, Commands)",
      "B2\tPast perfect continuous and sequencing events",
      "B2\tMixed Conditionals",
      "B2\tGradable and ungradable adjectives",
      "B2\tConditionals – If-Alternatives",
      "B2\tRelative Clauses with Preposition Placement",
      "C1\tVerbs & Tenses: Subjunctive Mood",
      "C1\tImperative – 3rd Person (Let) / Don’t You / Pointing",
      "C1\tClauses & Questions",
      "C1\tConditionals – Imperatives",
      "C1\tConditionals – Reduced & Inverted",
      "C1\tInversion – Structure",
      "C1\tInversion – Negative Adverbials",
      "C1\tInversion – Restrictive Adverbials",
      "C1\tRelative Clauses – Some of Which / Many of Which",
      "C1\tAdverb Clauses for Focus – Whatever / Wherever / However",
      "C1\tNonfinite Clauses (Advanced)",
      "C1\tPassive Voice Clauses for Sentence Focus",
      "C1\tWide Range of Reporting Verbs – Academic Use",
      "C1\tEmphasis Expressions",
      "C1\tFronting for Emphasis",
      "C1\tModal Verbs: Dare",
      "C1\tModal Verbs for Expressing Opinions",
      "C1\tModal Verbs for Expressing Criticism & Disapproval",
      "C1\tComparatives & Superlatives with Intensifiers",
      "C1\tSuperlatives with Postmodifiers",
      "C1\tCompound Adjectives",
      "C1\tAdjectives After Nouns",
      "C1\tAdverbs with Prepositional Phrases",
      "C1\tExtreme Adverbs to Modify Non-gradable Adjectives",
      "C1\tConjunctions – and yet / in that / either…or / neither…nor",
      "C1\tDiscourse Markers",
      "C1\tDistancing",
      "C1\tHedging",
      "C1\tEllipsis & Substitution"
    ],
    es: [
      "A1\tNombres propios: identificación",
      "A1\tNombres comunes: clasificación",
      "A1\tNombres: plural y singular",
      "A1\tAdjetivos calificativos: concordancia",
      "A1\tGrados del adjetivo: positivo/superlativo",
      "A1\tArtículo definido: identificación",
      "A1\tArtículo indefinido: primera mención",
      "A1\tDemostrativos: deixis",
      "A1\tPosesivos: posesión",
      "A1\tCuantificadores: cantidad",
      "A1\tPronombres personales: sujeto",
      "A1\tPronombres de objeto: formas",
      "A1\tPronombre relativo: que",
      "A1\tInterrogativos: forma",
      "A1\tAdverbios: lugar/tiempo",
      "A1\tLocuciones adverbiales: frecuencia",
      "A1\tPreposiciones: uso",
      "A1\tConjunciones: aditivas",
      "A1\tInfinitivo: uso",
      "A1\tVerbos: presente",
      "A1\tVerbos reflexivos: forma",
      "A1\tVerbo gustar: construcción",
      "A1\tHaber: existencia",
      "A1\tSer/estar: uso",
      "A1\tTener: posesión",
      "A1\tIr: movimiento",
      "A1\tQuerer: voluntad",
      "A1\tDeber: obligación",
      "A1\tHaber impersonal: existencia",
      "A1\tDistributivas: coordinación",
      "A1\tFinales: propósito",
      "A2\tSustantivos propios: topónimos/instituciones",
      "A2\tSustantivos comunes: eventivos",
      "A2\tAdjetivos calificativos: valoración",
      "A2\tGrados del adjetivo: comparativo",
      "A2\tGrados del adjetivo: superlativo relativo",
      "A2\tArtículo definido: anáfora",
      "A2\tArtículo indefinido: especificación",
      "A2\tDemostrativos: contraste",
      "A2\tPosesivos: énfasis",
      "A2\tCuantificadores: intensidad",
      "A2\tPronombres indefinidos",
      "A2\tInterrogativos/exclamativos: énfasis",
      "A2\tAdverbios interrogativos: información",
      "A2\tAdverbios: predicado",
      "A2\tAdverbios: modo",
      "A2\tAdverbios relativos/interrogativos",
      "A2\tPresente de indicativo: habitual/futuro",
      "A2\tPretérito imperfecto: descripción/hábito",
      "A2\tPretérito indefinido: pasado puntual",
      "A2\tPretérito perfecto: pasado reciente",
      "A2\tImperativo: instrucción/cortesía",
      "A2\tInfinitivo: funciones oracionales",
      "A2\tGerundio: progresivo",
      "A2\tParticipio: atributo",
      "A2\tSintagma nominal: complementos",
      "A2\tSintagma adjetival: modificadores",
      "A2\tSintagma verbal: complementos",
      "A2\tOración simple: tipos",
      "A2\tOraciones compuestas: coordinación",
      "A2\tOraciones subordinadas: sustantivas/adjetivas/adverbiales",
      "B1\tSustantivos propios: referencia cultural",
      "B1\tSustantivos comunes: abstracción",
      "B1\tGénero de los sustantivos: variación",
      "B1\tNúmero de los sustantivos: irregularidad",
      "B1\tAdjetivos: valoración",
      "B1\tArtículo definido: genérico",
      "B1\tArtículo indefinido: especificidad",
      "B1\tAusencia de determinación: valor nominal",
      "B1\tDemostrativos: discursivo",
      "B1\tPosesivos: afectividad",
      "B1\tCuantificadores: foco",
      "B1\tPronombre personal: énfasis",
      "B1\tPronombres relativos: especificación",
      "B1\tAdverbios: matiz",
      "B1\tPresente de indicativo: narrativo",
      "B1\tPretérito imperfecto: descripción",
      "B1\tPretérito indefinido: secuencia",
      "B1\tFuturo imperfecto: probabilidad",
      "B1\tCondicional simple: cortesía",
      "B1\tPretérito perfecto: experiencia",
      "B1\tPretérito pluscuamperfecto: anterioridad",
      "B1\tSubjuntivo: recomendación",
      "B1\tImperativo: instrucción",
      "B1\tInfinitivo: propósito",
      "B1\tGerundio: simultaneidad",
      "B1\tParticipio: anterioridad",
      "B1\tSintagma nominal/adjetival: expansión",
      "B1\tSintagma verbal: modificación",
      "B1\tOración simple: extensión",
      "B1\tOraciones coordinadas: adición/oposición",
      "B1\tOraciones subordinadas: especificación",
      "B2\tSustantivos propios: referencia específica",
      "B2\tAdjetivo: matiz valorativo",
      "B2\tPosición del adjetivo: significado",
      "B2\tGrados del adjetivo: gradación",
      "B2\tArtículo definido: especificación",
      "B2\tArtículo indefinido: referencia inespecífica",
      "B2\tAusencia de determinación: valor genérico",
      "B2\tDemostrativos: deixis discursiva",
      "B2\tPosesivos: énfasis contrastivo",
      "B2\tCuantificadores propios: intensidad",
      "B2\tCuantificadores focales: foco enfático",
      "B2\tPronombre personal: referencia discursiva",
      "B2\tPronombres relativos: precisión",
      "B2\tInterrogativos: matiz modal",
      "B2\tExclamativos: intensificación",
      "B2\tAdverbios: modalización",
      "B2\tLocuciones adverbiales: énfasis",
      "B2\tPresente de indicativo: proyección",
      "B2\tPretérito imperfecto: cortesía",
      "B2\tCondicional simple: hipótesis",
      "B2\tPretérito pluscuamperfecto: anterioridad narrativa",
      "B2\tFuturo perfecto: probabilidad",
      "B2\tCondicional compuesto: irrealidad",
      "B2\tSubjuntivo: duda",
      "B2\tSubjuntivo imperfecto: cortesía",
      "B2\tSubjuntivo perfecto: valoración",
      "B2\tSubjuntivo pluscuamperfecto: irrealidad",
      "B2\tImperativo: matiz exhortativo",
      "B2\tInfinitivo: valor modal",
      "B2\tGerundio: posterioridad",
      "B2\tParticipio: valor resultativo",
      "B2\tSintagma verbal: perspectiva",
      "B2\tOración simple: densidad",
      "B2\tCoordinadas: contraste",
      "B2\tSubordinadas: integración",
      "C1\tNombres propios: referencia social",
      "C1\tAdjetivo: precisión léxica",
      "C1\tArtículo definido: referencia discursiva",
      "C1\tArtículo indefinido: interpretación",
      "C1\tDemostrativos: deixis textual",
      "C1\tPosesivos: matiz afectivo",
      "C1\tCuantificadores propios: restricción",
      "C1\tCuantificadores focales: énfasis",
      "C1\tPronombre personal: distancia enunciativa",
      "C1\tPronombres relativos: selección",
      "C1\tInterrogativos: alcance",
      "C1\tExclamativos: énfasis afectivo",
      "C1\tAdverbios: valoración modal",
      "C1\tAdverbios del modus: valoración",
      "C1\tInterrogativos/exclamativos: matiz pragmático",
      "C1\tPresente de indicativo: valor discursivo",
      "C1\tPretérito imperfecto: reinterpretación",
      "C1\tFuturo imperfecto: inferencia",
      "C1\tCondicional simple: conjetura",
      "C1\tPretérito perfecto: relevancia",
      "C1\tPluscuamperfecto: anterioridad",
      "C1\tFuturo perfecto: deducción",
      "C1\tCondicional compuesto: hipótesis",
      "C1\tSubjuntivo presente: actitud",
      "C1\tSubjuntivo imperfecto: irrealidad",
      "C1\tGerundio: valor discursivo",
      "C1\tParticipio: valor absoluto",
      "C1\tImperativo: modulación",
      "C1\tInfinitivo: modalidad",
      "C1\tSintagma nominal: reestructuración",
      "C1\tSintagma adjetival: modificación",
      "C1\tSintagma verbal: variación aspectual",
      "C1\tOración simple: densidad informativa",
      "C1\tCoordinadas: conexión argumentativa",
      "C1\tSubordinadas: función argumentativa"
    ],
    de: [
      "A1\tDer Nominativ",
      "A1\tPluralbildung der Substantive",
      "A1\tArtikel in Nominativ",
      "A1\tPersonalpronomen",
      "A1\tVerben sein und haben",
      "A1\tRegelmäßige Verben im Präsens",
      "A1\tSatzstruktur: SVO",
      "A1\tKonjunktionen und, oder, aber",
      "A1\tFragewörter  wer, was, und wie",
      "A1\tNegation mit nicht und kein",
      "A1\tModale Verben mögen, wollen, können",
      "A1\tPossessivartikeln",
      "A1\tPredikative Adjektive",
      "A1\tPräpositionen mit Akkusativ",
      "A1\tFragewörter mit Akkusativ",
      "A1\tVerben mit Vokalwechsel im Präsens",
      "A1\tTrennbare und untrennbare Verben",
      "A1\tSatzbau bei trennbaren Verben",
      "A1\tWortstellung in Fragen",
      "A1\tAdjektivdeklination im Nominativ",
      "A1\tOrts- und Zeitadverbien",
      "A1\tDirekten Objektpronomen mit Akkusativ",
      "A1\tArtikel und Substantive im Akkusativ",
      "A1\tPräpositionen mit Dativ und Akkusativ",
      "A1\tVerben mit Dativ und Akkusativ",
      "A1\tSatzbau mit modale Verben",
      "A1\tKoordinierende Konjunktionen denn, sondern, doch und mal… mal",
      "A1\tZeitangaben mit am, im, um",
      "A1\tFutur I mit werden",
      "A1\tVergleichssätze",
      "A2\tDer Dativ",
      "A2\tDer Genitiv",
      "A2\tVerben mit festem Dativ",
      "A2\tReflexive Verben",
      "A2\tPossessivpronomen im Dativ und Akkusativ",
      "A2\tPräpositionen mit Dativ",
      "A2\tKorrelative Konjunktionen",
      "A2\tPerfekt mit haben",
      "A2\tPerfekt mit sein",
      "A2\tBildung des Partizip II",
      "A2\tAdjektive: Deklination mit bestimmtem Artikel",
      "A2\tAdjektive: Deklination mit unbestimmtem Artikel",
      "A2\tDer Superlativ",
      "A2\tNebensätze mit weil und dass",
      "A2\tModale Verben im Perfekt",
      "A2\tReflexive Verben in Präsens und Perfekt.",
      "A2\tReziproke Verben",
      "A2\tNebensätze mit wenn, ob, obwohl, bevor und nachdem",
      "A2\tKonjunktionen für Ursache und Gegensatz.",
      "A2\tInfinitiv mit zu",
      "A2\tInfinitiv ohne zu mit Modalverben",
      "A2\tPronominaladverbien darauf, daran, damit.",
      "A2\tVerben mit fester Präposition denken an, warten auf.",
      "A2\tRelativsätze im Nominativ",
      "A2\tRelativpronomen im Nominativ",
      "A2\tMengenangaben mit viel, wenig, einige und mehrere.",
      "A2\tAdverbien des Vergleichs: Mehr, weniger und lieber.",
      "A2\tBesonderheiten bei Präsens und Perfekt",
      "A2\tPräteritum von sein und haben",
      "A2\tSatzverbindungen mit doppeltem Verb",
      "B1\tRegelmäßige Verben im Präteritum",
      "B1\tUnregelmäßige Verben  im Präteritum",
      "B1\tPlusquamperfekt",
      "B1\tFutur I für Absicht und Vermutung.",
      "B1\tVorgangspassiv im Präsens",
      "B1\tZustandspassiv im Präsens",
      "B1\tRezipientenpassiv im Präsens",
      "B1\tKonjunktiv II fur Irrealität und Wunsch",
      "B1\tBedingungssätze mit wenn, falls und sofern",
      "B1\tRelativsätze mit Präpositionen",
      "B1\tInfinitivsätze mit zu und trennbaren Verben",
      "B1\tVerben der Wahrnehmung: Sehen, hören, fühlen",
      "B1\tFolgesätze mit so…dass und damit",
      "B1\tAdverbien des Modus und Grades",
      "B1\tInversion und Verbposition bei Fokus",
      "B1\tPartizip I und II als Adjektiv",
      "B1\tNominalisierung von Verben",
      "B1\tInfinitivkonstruktionen um… zu, ohne…  zu und anstatt... zu",
      "B1\tZeitkongruenz in komplexen Texten",
      "B1\tRelativpronomen welcher, was und was für ein",
      "B1\tZweiteiteilige Konjunktionen",
      "B1\tHäufige Wortkombinationen",
      "B1\tVerben mit Genitiv",
      "B1\tVerneinung  mit keineswegs, überhaupt nicht und niemals.",
      "B1\tModale Verben in der Vergangenheit",
      "B1\tVerben mit doppelter Bedeutung",
      "B1\tSubstantivierte Adjektive",
      "B1\tUrsache–Wirkung-Konstruktionen",
      "B1\tIndirekte Rede im Präsens",
      "B1\tNebensätzen mit sodass, falls, obgleich, obschon und indem.",
      "B2\tKonjunktiv I im indirekten Stil",
      "B2\tDer Konjunktiv II mit Modalverben",
      "B2\tDer Futur II",
      "B2\tDer Passiv in allen Zeiten",
      "B2\tStellungsregel für Dativ- und Akkusativpronomen",
      "B2\tN-Deklination und Sonderfälle",
      "B2\tPräpositionen mit Genitiv",
      "B2\tVerben mit figurativer Bedeutung",
      "B2\tKommentaradverben",
      "B2\tGradpartikeln",
      "B2\tModalpartikeln: kommunikative Nuancen – Nuancen im Gespräch.",
      "B2\tProportionale Vergleiche mit je…desto und umso…",
      "B2\tSatzbau mit lassen",
      "B2\tZusammengesetzte Zeiten",
      "B2\tSatzverbindunge,  Konnektoren und Absatzstruktur",
      "B2\tIndirekte Rede in allen Zeiten",
      "B2\tPassiv mit Modalverben",
      "B2\tUmformung von Sätzen mit Infinitivsatz",
      "B2\tPartizipien als Verbergänzungen",
      "B2\tUnpersönliche Konstruktionen",
      "B2\tKombination von Konjunktiv I und II",
      "B2\tReflexive Verben mit doppelter Bedeutung",
      "B2\tAdjektivdeklination in gemischten Fällen",
      "B2\tAdverbien zur Verstärkung und Bewertung",
      "B2\tKomposita: Verbindungsregeln.",
      "B2\tWortfamilien und Ableitung",
      "B2\tAntonyme und Konversion",
      "B2\tLogische Konnektoren",
      "B2\tSprachökonomie: Ersatz von Substantiven und Ellipse",
      "B2\tFormeller vs. informeller Stil",
      "C1\tIndirekte Rede im Journalismus",
      "C1\tPartizipialgruppen mit eigenen Ergänzungen",
      "C1\tFutur II für Vermutungen über die Vergangenheit",
      "C1\tPartizip I und II als Attribute",
      "C1\tPrädikativkonstruktionen im Stilmittelgebrauch",
      "C1\tGenitivgebrauch im schriftlichen Deutsch",
      "C1\tWechselpräpositionen in abstrakten Kontexten",
      "C1\tMehrfache Nebensätze und Verschachtelung.",
      "C1\tModal- und Gradpartikeln im Gespräch und Schrift.",
      "C1\tRelativsätze mit Genitiv",
      "C1\tKonjunktionaladverbien",
      "C1\tStilistische Variation: Aktiv/Passiv",
      "C1\tVerbklammer und Satzklammer bei längeren Sätzen",
      "C1\tAbstrakte Nominalisierungen",
      "C1\tBedeutungsnuancen wischen Wörtern oder Formulierungen",
      "C1\tSubjektive Bedeutung von Modalverben",
      "C1\tInversion zur Betonung: Hypotaxe,Parataxe",
      "C1\tFutur II für Annahmen über die Vorvergangenheit",
      "C1\tPassiv im wissenschaftlichen Stil",
      "C1\tNominalisierung als Mittel zur Absatzkohärenz",
      "C1\tMehrgliedrige Nominalphrasen im Genitiv",
      "C1\tVerschachtelungen mit dem Genitiv",
      "C1\tAdjektiv- und Adverbialgebrauch in Fachtexten",
      "C1\tFunktionsverbgefüge",
      "C1\tMehrfach verschachtelte Relativsätze und Infinitivsätze.",
      "C1\tSubtile Bedeutungsunterschiede bei Modal- und Gradpartikeln für Nuancen",
      "C1\tSprachliche Kettenglieder und Kohärenz in längeren Texten",
      "C1\tKonjunktionaladverbien und stilistische Feinheiten",
      "C1\tTopologisches Feldermodell (Vorfeld, Mittelfeld, Nachfeld)",
      "C1\tModalpassiv im wissenschaftlichen Stil"
    ],
    fr: [
      'A1\tPrésent de l\'indicatif',
      'A1\tArticles définis et indéfinis',
      'A1\tAdjectifs possessifs',
      'A1\tNégation (ne...pas)',
      'A2\tPassé composé',
      'A2\tImparfait',
      'A2\tFutur simple',
      'A2\tPronom "y" et "en"',
      'B1\tPlus-que-parfait',
      'B1\tConditionnel présent',
      'B1\tSubjonctif présent',
      'B1\tDiscours indirect',
      'B2\tConditionnel passé',
      'B2\tSubjonctif passé',
      'B2\tVoix passive',
      'B2\tGérondif et participe présent',
      'C1\tSubjonctif imparfait',
      'C1\tPassé simple',
      'C1\tAnglicismes et gallicismes',
      'C2\tRegistres de langue',
    ],
    it: [
      "A1\tPronuncia: alfabeto",
      "A1\tPronomi personali soggetto",
      "A1\tArticoli: uso base",
      "A1\tNomi: genere/numero",
      "A1\tAggettivi: concordanza",
      "A1\tPresente: regolari",
      "A1\tPresente: irregolari comuni",
      "A1\tPresente: andare/venire",
      "A1\tPresente: uscire",
      "A1\tModali: uso base",
      "A1\tC’è/Ci sono",
      "A1\tNumeri/date/ore",
      "A1\tDimostrativi: questo/quello",
      "A1\tPreposizioni semplici",
      "A1\tPreposizione Da",
      "A1\tPreposizioni: articolate/composte",
      "A1\tPreposizioni: luogo/tempo",
      "A1\tPossessivi: base",
      "A1\tPronomi diretti: forma",
      "A1\tPiacere: presente",
      "A1\tPerché/per: motivo/scopo",
      "A1\tInterrogative: dirette/indirette",
      "A1\tDomande: quanto",
      "A1\tQuantità: di",
      "A1\tAvverbi di frecuencia",
      "A1\tNegazione: mai",
      "A1\tAnche/neanche: uso",
      "A1\tImperativo: tu",
      "A1\tEssere/stare: differenza",
      "A1\tConnettori: base",
      "A2\tVerbi in -isc",
      "A2\tPassato prossimo: formazione",
      "A2\tParticipi passati: irregolari",
      "A2\tPassato prossimo: riflessivi",
      "A2\tPassato prossimo: modali",
      "A2\tImperfetto: uso",
      "A2\tPassato vs imperfetto",
      "A2\tEspressioni temporali: passato",
      "A2\tPresente storico: uso",
      "A2\tFuturo semplice: uso",
      "A2\tStare + gerundio",
      "A2\tCondizionale semplice: regolare / irregolare",
      "A2\tPronomi indiretti: forma",
      "A2\tPassato prossimo: pronomi diretti",
      "A2\tPronomi combinati: uso",
      "A2\tPronome ne: uso",
      "A2\tPronome ci: uso",
      "A2\tPronomi relativi: base",
      "A2\tAggettivi / pronomi: indefiniti",
      "A2\tQualcuno / nessuno / ognuno",
      "A2\tArticoli + professioni",
      "A2\tComparativi: forme / uso",
      "A2\tComparativi: irregolari",
      "A2\tSuperlativo: forme / uso",
      "A2\tIpotetica 1° tipo",
      "A2\tInfinito: imperativo",
      "A2\tImperativo: noi/voi",
      "A2\tVerbi + preposizione",
      "A2\tVolerci/metterci: base",
      "A2\tCi vuole/ci vogliono",
      "B1\tPassato prossimo: narrazione/cronologia",
      "B1\tTrapassato prossimo: narrazione",
      "B1\tFuturo anteriore: anticipazione",
      "B1\tTempi composti: idiomi",
      "B1\tCongiuntivo presente: uso",
      "B1\tCongiuntivo passato: uso",
      "B1\tCongiuntivo imperfetto: uso",
      "B1\tCome se: congiuntivo",
      "B1\tIpotetica 2° tipo: irrealtà",
      "B1\tCondizionale passato",
      "B1\tCondizionale: cortesia/opinione",
      "B1\tDovere: supposizioni",
      "B1\tPronomi combinati: avanzato",
      "B1\tRelativi: preposizione",
      "B1\tRelative: preposizione",
      "B1\tCi/Ne: avanzato",
      "B1\tDato che / poiché / siccome: uso",
      "B1\tCongiunzioni temporali: uso",
      "B1\tCongiunzioni  concessive: congiuntivo",
      "B1\tPrima che: congiuntivo",
      "B1\t“Anche se” + indicativo",
      "B1\tCondizionali: uso",
      "B1\tConnettori: discorsivi",
      "B1\tBasta + infinito",
      "B1\tRipetizione emfática: uso",
      "B1\tVerbi pronominali: uso",
      "B1\tVerbi supporto: Espressioni idiomatiche",
      "B1\tQuantità/intensità",
      "B1\tPassiva: essere/venire",
      "B1\tGerundio seplice/composto: subordinato",
      "B2\tPassato remoto: narrativo/storico",
      "B2\tTrapassato remoto:narrativa",
      "B2\tDoppio Ausiliare: essere/avere",
      "B2\tInfinito passato: uso",
      "B2\tIpotetica 3° tipo",
      "B2\tCondizionale passato: ipotesis / narrazione",
      "B2\tCondizionale: presente vs passato",
      "B2\tCondizionale:discorso indiretto",
      "B2\tConcordanza dei tempi complessi",
      "B2\tConcordanza congiuntivo: uso",
      "B2\tSubordinazione: a condizione che/ a patto che/ salvo che",
      "B2\tFrasi finali: congiuntivo",
      "B2\tFrasi concessive: congiuntivo",
      "B2\tFrasi comparative: congiuntivo",
      "B2\tFrasi relative: congiuntivo",
      "B2\tFrasi indipendenti:  congiuntivo",
      "B2\tGerundio pasado: uso",
      "B2\tPur + gerundio",
      "B2\tInfinito: pur di / anziché / invece di",
      "B2\tInfinito:  presente/passato",
      "B2\tCostruzioni causative: uso",
      "B2\tCostruzioni passive: venire/andare",
      "B2\tForme impersonali:  si dice che sia/si pensa che abbia",
      "B2\tEnfasi con È",
      "B2\tDiscorso indiretto: avanzato",
      "B2\tAlterazioni dei nomi: -ino/ -one/ -uccio/ -accio",
      "B2\tNomi e aggettivi composti",
      "B2\tPrefissi e suffissi:valore semantico",
      "B2\tPronominali e riflessivi: valore idiomatico",
      "B2\tConnettivi: logico-testuali",
      "C1\tCongiuntivo: frasi subordinate implicite",
      "C1\tConcordanza: tempi + congiuntivo",
      "C1\tConcordanza: condizionale presente/passato",
      "C1\tCongiuntivo trapassato",
      "C1\tPeriodo ipotetico: inversione / ellissi",
      "C1\tPeriodo ipotetico: mistas",
      "C1\tPeriodo ipotetico: implicito",
      "C1\tSe: valori temporale / causale",
      "C1\tInfinito implicito",
      "C1\tDa  / per + infinito",
      "C1\tParticipio presente/passato: subordinante",
      "C1\tParticipio assoluto ed ellittico",
      "C1\tGerundio: causale/ temporale / concessivo",
      "C1\tInfinito/participio: sostituti di subordinate",
      "C1\tImperativo: discorso indiretto",
      "C1\tStilo indiretto: registro/cortesia",
      "C1\tPronomi doppi relativi",
      "C1\tNon: pleonastico",
      "C1\tChe: preposizione",
      "C1\tChe polivalente: discorso",
      "C1\tCostrutti enfatici / tematizzazione",
      "C1\tPreposizioni: idiomatiche",
      "C1\tRegionalismi: verbali",
      "C1\tRafforzativi: uso",
      "C1\tAggettivi: idiomatici",
      "C1\tSuperlativi: idiomatici",
      "C1\tAvverbi discorsivi",
      "C1\tNominalizzazioni",
      "C1\tFronting / inversione",
      "C1\tSegnali discorsivi"
    ],
    pt: [
      'A1\tPresente do Indicativo',
      'A1\tArtigos definidos e indefinidos',
      'A1\tPronomes pessoais',
      'A1\tGénero e número',
      'A2\tPretérito Perfeito',
      'A2\tPretérito Imperfeito',
      'A2\tFuturo do Presente',
      'A2\tComparativos e Superlativos',
      'B1\tPretérito Mais-que-Perfeito',
      'B1\tCondicional',
      'B1\tPresente do Conjuntivo',
      'B1\tImperativo',
      'B2\tImperfeito do Conjuntivo',
      'B2\tFuturo do Conjuntivo',
      'B2\tVoz Passiva',
      'B2\tDiscurso Indireto',
      'C1\tMais-que-Perfeito do Conjuntivo',
      'C1\tInfinitivo Pessoal',
      'C1\tOrações Condicionais',
      'C2\tRegistos de língua',
    ],
    'pt-BR': [
      'A1\tPresente do Indicativo',
      'A1\tArtigos definidos e indefinidos',
      'A1\tPronomes pessoais',
      'A1\tGênero e número',
      'A2\tPretérito Perfeito',
      'A2\tPretérito Imperfeito',
      'A2\tFuturo do Presente',
      'A2\tComparativos e Superlativos',
      'B1\tPretérito Mais-que-Perfeito',
      'B1\tCondicional',
      'B1\tPresente do Subjuntivo',
      'B1\tImperativo',
      'B2\tImperfeito do Subjuntivo',
      'B2\tFuturo do Subjuntivo',
      'B2\tVoz Passiva',
      'B2\tDiscurso Indireto',
      'C1\tMais-que-Perfeito do Subjuntivo',
      'C1\tInfinitivo Pessoal',
      'C1\tOrações Condicionais',
      'C2\tRegistros de linguagem',
    ],
    tr: [
      'A1\tGeniş Zaman',
      'A1\tİsim Tamlamaları',
      'A1\tÇoğul Ekleri',
      'A1\tŞimdiki Zaman',
      'A2\tGeçmiş Zaman (-di)',
      'A2\tGelecek Zaman (-ecek)',
      'A2\tSıfat Fiiller',
      'A2\tZarf Fiiller',
      'B1\tBelirli Geçmiş Zaman (-miş)',
      'B1\tŞart Kipi',
      'B1\tEdilgen Çatı',
      'B1\tDolaylı Anlatım',
      'B2\tBileşik Zamanlar',
      'B2\tİstek Kipi',
      'B2\tEttirgen Çatı',
      'B2\tBağlaçlar',
      'C1\tOrtaç Yapıları',
      'C1\tUlaç Yapıları',
      'C1\tYazı Dili Özellikleri',
      'C2\tResmi ve Edebi Dil',
    ],
  } as Record<LanguageCode, string[]>,
} as const;

/**
 * Get default values for economic sector
 */
export function getEconomicSectorDefaults(): string[] {
  return [...DEFAULT_ALLOWED_VALUES.economic_sector];
}

/**
 * Get default values for grammatical reference by language
 */
export function getGrammaticalReferenceDefaults(language: LanguageCode): string[] {
  return [...(DEFAULT_ALLOWED_VALUES.grammatical_reference[language] || [])];
}
